import { touchFloor, touchSlop } from '../_touchTarget.js'

// Assistant markdown renders through the shared registered function
// `renderAssistantMarkdown` (symbolize-markdown), invoked via el.call in
// Md.children — el.call is brender-safe (a runtime string lookup), and the
// renderer + its demote map live in shared/functions/renderAssistantMarkdown.js
// so docs, preview and workspace render assistant markdown identically.

export const ChatMessage = {
  // FA204 / frank trap — initial/themeForRole used to be bare module-scope
  // const read from inside Avatar.theme/Avatar.text (serialized factories);
  // they read as undefined once this component is frank.toJSON'd for
  // platform delivery. Moved into `scope`, called via `el.scope.<name>`.
  scope: {
    initial(name) {
      return (name || '?')[0].toUpperCase()
    },
    themeForRole(role) {
      return role === 'user'
        ? 'primary'
        : role === 'system'
          ? 'warning'
          : role === 'tool'
            ? 'info'
            : 'success'
    },

    // AIUX-CHROME item 4 (tickets/sonnet.md, live-verified 2026-08-13) — on a
    // synthesized failure answer (failWorkspaceLoop; ChatMessage's own
    // `onInit`/`onUpdate` comment above documents the shape), the server puts
    // its RAW diagnostic straight into the message's own `text` — proven live:
    // "The Symbols Service (managed AI) is currently unavailable — Symbols
    // Service produced no output — empty SSE stream from
    // https://symbols-service-production.up.railway.app/api/chat (no deltas,
    // no tool calls, no error frame)…", `metadata.errorCode:
    // 'AI_PROVIDER_UNAVAILABLE'`. That string (stream internals, a raw
    // backend URL) is developer copy, never user copy. Only `errorCode` is a
    // safe, structured signal — never echo `metadata.errorMessage` /
    // `s.text` for a non-credits error. Credits-exhaustion codes keep their
    // OWN dedicated banner + copy (notifyCreditsExhaustedFromMessage) — this
    // returns null for those so the caller falls back to the normal text.
    //
    // `memberText: true` (an unkept-promise turn, `errorCode:
    // 'TURN_ENDED_ON_PROMISE'`) marks text the server wrote FOR the member:
    // the model's own words plus a stop line in the member's language. That
    // row shows its text as written (null → the caller renders `s.text`);
    // an empty text still gets the generic line below, so no row is blank.
    humanErrorText(metadata, tools, text) {
      if (metadata && metadata.memberText === true && String(text || '').trim()) return null
      const code = String((metadata && metadata.errorCode) || '')
      if (['INSUFFICIENT_CREDITS', 'SPEND_CAP_EXCEEDED', 'ALLOCATION_EXCEEDED'].includes(code)) {
        return null
      }
      // A turn can fail AFTER its tools already ran: the write landed and only
      // the summarizing model call came back empty. Reporting that as "the AI
      // service is unavailable" tells the user nothing happened when in fact
      // something did — measured 2026-08-14, three of eight `create_note` turns
      // created the document and still rendered a bare failure, with the row's
      // own `tools[]` carrying "Done: create_note completed successfully"
      // right next to the denial. Lead with what completed.
      const done = (tools || []).filter((t) => t && t.status === 'done')
      const failed = (tools || []).filter((t) => t && t.status === 'error')
      // AI-301 — when the row knows WHAT got made (t.created, stamped by the
      // transport), name the actual item; the humanized tool name is the
      // fallback for tools without a structured outcome.
      const nameTools = (list) =>
        list
          .map((t) =>
            t.created && (t.created.title || t.created.name)
              ? `“${String(t.created.title || t.created.name)}”`
              : String(t.tool || '').replace(/_/g, ' ')
          )
          .join(', ')
      // A failed tool (status 'error', set by the transport on ok:false) is the
      // failure itself. Name it. Never blame the summary for it.
      if (failed.length) {
        if (done.length) {
          return (
            `${nameTools(failed)} failed, so this answer stopped. ${nameTools(done)} finished and is saved. ` +
            'Ask me to continue when you are ready.'
          )
        }
        return `${nameTools(failed)} failed. Try again, or switch models if it keeps happening.`
      }
      if (done.length) {
        return (
          `I finished ${done.length === 1 ? 'this action' : 'these actions'} — ${nameTools(done)} — but ` +
          "couldn't write the summary afterwards. That work is saved; nothing needs redoing. " +
          'Ask me to continue when you are ready.'
        )
      }
      if (code === 'AI_PROVIDER_UNAVAILABLE') {
        return 'The AI service is temporarily unavailable. Try again in a moment, or switch models.'
      }
      return 'Something went wrong answering that. Try again, or switch models if it keeps happening.'
    },

    // Is this row a non-credits synthesized-error answer with a retry/switch
    // affordance worth showing? (credits errors get their own CTA via the
    // notification banner, not an inline retry.)
    isRetryableError(s) {
      if (!s || s.role !== 'assistant' || s.streaming) return false
      const metadata = s.metadata
      if (!metadata || metadata.kind !== 'error') return false
      const code = String(metadata.errorCode || '')
      if (['INSUFFICIENT_CREDITS', 'SPEND_CAP_EXCEEDED', 'ALLOCATION_EXCEEDED'].includes(code)) {
        return false
      }
      // NEVER offer retry once a tool has already run in this turn. The server
      // marks provider emptiness `retryable: true`, but a retry re-runs the
      // whole turn — and the tools that already succeeded run a SECOND time.
      // Measured 2026-08-14: one turn silently created the same document twice.
      // Continuing is safe; retrying is not.
      if ((s.tools || []).some((t) => t && t.status === 'done')) return false
      return metadata.retryable !== false
    },

    // AI-304 (tickets/ai.md) — the failed-AFTER-tools case Retry deliberately
    // abandons: work landed, only the summarizing turn died, and the user had
    // no one-click way forward. Continue sends a resume prompt through the
    // normal send path; the conversation's persisted tool results give the
    // model its state, and duplicate-safety is AI-202's server-side
    // idempotency replay (an identical re-issued write returns the ORIGINAL
    // result instead of a second row). Credits errors stay excluded — their
    // banner owns the CTA.
    isContinuableError(s) {
      if (!s || s.role !== 'assistant' || s.streaming) return false
      const metadata = s.metadata
      if (!metadata || metadata.kind !== 'error') return false
      const code = String(metadata.errorCode || '')
      if (['INSUFFICIENT_CREDITS', 'SPEND_CAP_EXCEEDED', 'ALLOCATION_EXCEEDED'].includes(code)) {
        return false
      }
      return (s.tools || []).some((t) => t && t.status === 'done')
    },

    // AI-302 (tickets/ai.md) — the render-time gate: tool-call protocol text
    // must never render as the answer, in EVERY ChatMessage host. The
    // workspace app already guards at its state layer (simoneAiTransport +
    // sessions._toChatMessages, functions/_simone/protocolText.js — the
    // canonical, tested copy); docs/preview mount this component with their
    // own transports and get THIS last line of defense. Deliberately a
    // compact INLINE twin of that helper — scope methods must be
    // self-contained for frank/brender serialization, so an import cannot
    // express it; keep the two in step when either changes. Conservative by
    // the same rules: fenced code and JSON-bearing prose are never flagged;
    // whole-text call shapes, wire markers, and the measured truncated
    // fragment are. Returns the neutral notice, or null for a clean text.
    protocolLeakNotice(text) {
      const t = String(text || '').trim()
      if (!t || t.startsWith('```')) return null
      const NOTICE =
        'This reply contained internal tool instructions instead of an answer — ' +
        'the raw output was kept for diagnosis. Ask again if anything is missing.'
      if (t.startsWith('{') && t.endsWith('}')) {
        try {
          const p = JSON.parse(t)
          if (p && typeof p === 'object' && !Array.isArray(p)) {
            const bag = p.tool_call && typeof p.tool_call === 'object' ? p.tool_call : p
            const isCall =
              typeof bag.name === 'string' &&
              bag.name.trim() &&
              (p.tool_call !== undefined ||
                Object.prototype.hasOwnProperty.call(bag, 'arguments') ||
                Object.prototype.hasOwnProperty.call(bag, 'args'))
            if (isCall) return NOTICE
          }
        } catch (_) {
          /* unparseable — fall through to the fragment rules */
        }
        if (/^\{\s*"tool_call"\s*:/.test(t)) return NOTICE
      }
      if (/^\[Tool call\]/.test(t) || /^RTLU to=functions\./.test(t)) return NOTICE
      const fragment =
        /^"[A-Za-z_][\w-]*"\s*:/.test(t) &&
        /[}\]]\s*$/.test(t) &&
        t.length <= 400 &&
        !/[.!?]\s+[A-Z]/.test(t)
      return fragment ? NOTICE : null
    },

    // ── GROUNDING (spec §4.2–4.3) ────────────────────────────────────────
    // Scope methods, not module-scope consts: the factories below are
    // serialized by frank.toJSON and a bare module read is `undefined` there
    // (the FA204 trap this whole file is organised around). Each method is
    // self-contained for the same reason — no method calls a sibling.

    // Band → semantic INK token.
    //
    // The band is resolved from whatever the server sent: `band` is the
    // authority when present, and a result that carried only a `score` still
    // resolves off the spec §4.3 thresholds (High ≥ .80 · Moderate ≥ .55 ·
    // Low ≥ .30 · Insufficient < .30), so the pill can never read "Unscored"
    // for an answer that WAS scored. `confBandKey` below repeats the ladder
    // rather than calling this one — sibling scope methods are reachable, but
    // each returns a different thing and neither is the other's helper.
    //
    // These four tokens are the adaptive light-dark pairs
    // from the design system (shared/designSystem/color.js) — a raw hue would
    // fail contrast on one of the two stages (Rule 67). An unscored row takes
    // `caption`, the house muted ink: quiet, but never invisible.
    confTone(confidence) {
      let band = ''
      if (confidence && typeof confidence === 'object') {
        const named = String(confidence.band || '').toLowerCase()
        if (['high', 'moderate', 'low', 'insufficient'].includes(named)) band = named
        else {
          const score = typeof confidence.score === 'number' ? confidence.score : null
          if (score != null) {
            band =
              score >= 0.8
                ? 'high'
                : score >= 0.55
                  ? 'moderate'
                  : score >= 0.3
                    ? 'low'
                    : 'insufficient'
          }
        }
      }
      const TONES = {
        high: 'successInk',
        moderate: 'accentInk',
        low: 'warningInk',
        insufficient: 'dangerInk'
      }
      return TONES[band] || 'caption'
    },

    // '0–1' → '72%'. Returns '' when there is no number, so the pill shows the
    // band label alone rather than a bare '%'.
    confPercent(confidence) {
      const score =
        confidence && typeof confidence === 'object' && typeof confidence.score === 'number'
          ? confidence.score
          : null
      if (score == null) return ''
      return Math.round(Math.max(0, Math.min(1, score)) * 100) + '%'
    },

    // The six weighted factors, in the spec's own order, as breakdown rows.
    // ALWAYS six — a factor the server omitted renders an empty bar rather
    // than vanishing, so the shape of the score stays readable and two
    // answers stay comparable.
    confRows(confidence) {
      const b = (confidence && typeof confidence === 'object' && confidence.breakdown) || {}
      // The polyglot key rides on the ROW as a LITERAL. Building it at the
      // render site (`'ai.confidence.factor.' + s.factor`) is banned: a key
      // assembled at runtime is invisible to grep, to the en/ka parity test
      // and to the extraction tooling (docs/I18N-CONVENTION.md §1.3, enforced
      // by i18nSharedExtraction's "no key is built by concatenation").
      const FACTORS = [
        ['precedent', 'ai.confidence.factor.precedent'],
        ['coverage', 'ai.confidence.factor.coverage'],
        ['authority', 'ai.confidence.factor.authority'],
        ['freshness', 'ai.confidence.factor.freshness'],
        ['consistency', 'ai.confidence.factor.consistency'],
        ['self', 'ai.confidence.factor.self']
      ]
      return FACTORS.map(([k, labelKey]) => {
        const raw = typeof b[k] === 'number' ? b[k] : null
        return {
          key: 'cf-' + k,
          factor: k,
          labelKey,
          value: raw,
          pct: raw == null ? 0 : Math.round(Math.max(0, Math.min(1, raw)) * 100)
        }
      })
    },

    // Band → its polyglot key, as LITERALS for the same reason as confRows
    // above. Returns '' for an unscored answer; the render site then reaches
    // for the 'unscored' key by its own literal.
    confBandKey(confidence) {
      let band = ''
      if (confidence && typeof confidence === 'object') {
        const named = String(confidence.band || '').toLowerCase()
        if (['high', 'moderate', 'low', 'insufficient'].includes(named)) band = named
        else {
          const score = typeof confidence.score === 'number' ? confidence.score : null
          if (score != null) {
            band =
              score >= 0.8
                ? 'high'
                : score >= 0.55
                  ? 'moderate'
                  : score >= 0.3
                    ? 'low'
                    : 'insufficient'
          }
        }
      }
      const BAND_KEYS = {
        high: 'ai.confidence.band.high',
        moderate: 'ai.confidence.band.moderate',
        low: 'ai.confidence.band.low',
        insufficient: 'ai.confidence.band.insufficient'
      }
      return BAND_KEYS[band] || ''
    },

    // ── THE ADVICE LEVEL (vault spec 06 §7) ─────────────────────────────
    // The server's level bag, read for the pill. Only a workspace that grades
    // its answers (`settings.gradientCeiling` on the server) sends one; every
    // other row carries `level: null` and this returns null, so the block
    // never renders there. NO POSITION, NO PILL: the drawer never guesses a
    // level. A refused answer is named Out of scope, never shown as a low
    // number. Keys ride as LITERALS, for the reason confRows gives.
    levelView(level) {
      if (!level || typeof level !== 'object') return null
      const num = (v) => {
        if (v === null || v === undefined || v === '') return null
        const n = Number(v)
        return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null
      }
      const refused = level.refused === true
      const emitted = num(level.emitted)
      if (!refused && emitted === null) return null
      const BANDS = [
        [3, 'ai.level.band.legalNote'],
        [6, 'ai.level.band.legalRecommendation'],
        [7, 'ai.level.band.legalDecision'],
        [8, 'ai.level.band.commercialNote'],
        [9, 'ai.level.band.commercialRecommendation'],
        [10, 'ai.level.band.commercialDecision']
      ]
      const band = refused ? null : BANDS.find(([top]) => emitted <= top)
      const ceilings = (Array.isArray(level.ceilings) ? level.ceilings : [])
        .map((c, i) => ({
          key: 'lc-' + i,
          text: String(
            typeof c === 'string' ? c : (c && (c.reason || c.text || c.condition)) || ''
          ).trim()
        }))
        .filter((c) => c.text)
      const r = level.retreat && typeof level.retreat === 'object' ? level.retreat : null
      return {
        refused,
        labelKey: refused ? 'ai.level.outOfScope' : band[1],
        value: refused ? '' : String(emitted),
        requested: num(level.requested),
        emitted,
        ceilings,
        retreat: r ? String(r.condition || r.reason || '').trim() : ''
      }
    },

    // ── A QUESTION UNDER ITS STEP (Nika 2026-10-28: "fix positioning") ──
    // A question card (choice / confirm) that names a step's call id
    // (`_callId`) is drawn under that step (ChatToolStep); every other card
    // stays in Cards. One rule for both, so a card is drawn exactly once.
    // `_step` tells the card a step row sits above it (a closed card leaves
    // the reason to that row); `_pickOnRow`, that the row names its answer
    // (the card's folded line then keeps the question and Change).
    stepAsks(cards, id, row) {
      if (id == null || !Array.isArray(cards)) return []
      const onRow = this.rowNamesPick(row)
      return cards
        .filter(
          (c) =>
            !!c &&
            (c.type === 'choice' || c.type === 'confirm') &&
            c._callId != null &&
            String(c._callId) === String(id)
        )
        .map((c, i) => ({
          key: 'ask-' + String(c._cardId || i),
          ...c,
          _step: true,
          _pickOnRow: onRow
        }))
    },
    // THE ANSWER IS SAID ONCE (coordinator W3a, 2026-10-29: "You chose
    // “Blue”" on the row, and "Blue" again on the card's folded line under
    // it). The row names the answer when its question is done and its
    // `answer` (the transport's stamp, a reload's restore) fits the row
    // whole: 48 characters, the row's own limit (ChatToolRow). A longer
    // answer reads "You answered" there and its card keeps the words; an
    // Edit has no `answer` (its card says what should change). Only a
    // question's row names an answer (the tools ChatToolRow's label reads).
    rowNamesPick(row) {
      const answer = row && typeof row.answer === 'string' ? row.answer.trim() : ''
      const tool = String((row && row.tool) || '').replace(/^module_fn_/, '')
      const asks = ['ask_user', 'askUser', 'confirm_with_user', 'confirmWithUser'].includes(tool)
      return asks && row.status === 'done' && answer !== '' && answer.length <= 48
    },
    // A step's row with the questions it asked (`_asks`). The drawn row is
    // the thread's row: an answered question's row names the pick ("You
    // chose “Yes”", ChatToolRow) from the `answer` the transport stamps and a
    // reload restores. It used to drop that `answer` whenever an answered
    // card sat under the step, so live every answered question read "You
    // answered" (F3 live, W11 dev 2026-10-29: confirm "Yes", ask "Blue").
    stepRow(t, cards) {
      return { key: t.id, ...t, _asks: this.stepAsks(cards, t && t.id, t) }
    },
    looseCards(s) {
      const cards = Array.isArray(s && s.cards) ? s.cards : []
      const tools = Array.isArray(s && s.tools) ? s.tools : []
      if (!cards.length) return []
      const steps = new Set(tools.map((t) => (t && t.id != null ? String(t.id) : '')))
      return cards.filter(
        (c) =>
          !(
            c &&
            s.role === 'assistant' &&
            (c.type === 'choice' || c.type === 'confirm') &&
            c._callId != null &&
            steps.has(String(c._callId))
          )
      )
    }
  },

  // Hide assistant rows that have no text and aren't streaming — otherwise
  // the Meta line (Author + Time) renders as a ghost row. A row that carries
  // cards or an agentic tool timeline is content too.
  if: (el, s) =>
    s.role !== 'assistant' ||
    !!s.text ||
    !!s.streaming ||
    !!(s.cards && s.cards.length) ||
    !!(s.tools && s.tools.length),

  // Credits-exhausted UX (tickets/opus.md) — the metadata-key check the
  // ticket calls for: `s.metadata` carries `{ kind:'error', errorCode, … }`
  // when this row is failWorkspaceLoop's synthesized answer (live turn: SDK
  // AiService → simoneAiTransport's onDone patch; reload: sessions.js
  // _toChatMessages). notifyCreditsExhaustedFromMessage (workspace/functions/
  // creditGuard.js) does the actual role-aware notification/banner work —
  // this is only the wiring, kept intentionally dumb and defensive: docs/
  // preview mount ChatMessage too and don't register that function, so a
  // missing-function throw must never break the row.
  //
  // Both onInit (a HISTORY-loaded row already carries metadata at creation —
  // onUpdate never fires for it) and onUpdate (a LIVE row's metadata lands
  // in a later patch, after the row already exists and is mid-stream) are
  // wired to the same call — notifyCreditsExhaustedFromMessage dedupes by
  // message id itself, so calling it from both is idempotent.
  onInit: (el, s) => {
    if (s.role === 'assistant' && s.metadata && s.metadata.kind === 'error') {
      try {
        el.call('notifyCreditsExhaustedFromMessage', el, s)
      } catch (_) {
        /* function not registered on this app (docs/preview) — no-op */
      }
    }
  },
  onUpdate: (el, s) => {
    if (s.role === 'assistant' && s.metadata && s.metadata.kind === 'error') {
      try {
        el.call('notifyCreditsExhaustedFromMessage', el, s)
      } catch (_) {
        /* function not registered on this app (docs/preview) — no-op */
      }
    }
  },

  flow: 'x',
  gap: 'Z',
  width: '100%',
  align: 'flex-start',
  flexDirection: (el, s) => (s.role === 'user' ? 'row-reverse' : 'row'),
  // Entrance — each new row rises in softly (one-shot: CSS animations run at
  // node creation only, and rows are keyed keep-alive, so streaming patches
  // never re-trigger it). Brand keyframe, ~iOS timing.
  animationName: 'fadeInUpShort',
  animationDuration: '340ms',
  animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',

  // Consecutive same-author rows (s._grouped, set in Chat.js → Messages
  // children) stack tightly: pull the row up against the list's 'A' gap.
  // Done via a static modifier — a reactive `marginTop: (el,s)=>…` token is a
  // spacing prop with a function value, which this bundle's DOMQL drops.
  isGrouped: (el, s) => !!s._grouped,
  '.isGrouped': { marginTop: 'calc(var(--spacing-A) * -0.55)' },

  Avatar: {
    // No avatar for assistant rows (full-width stacked block) nor for
    // continuation rows of a same-author group. User avatar lives in Meta so
    // it aligns with the "You" + timestamp line.
    if: (el, s) => s.role !== 'assistant' && s.role !== 'user' && !s._grouped,
    width: 'B',
    height: 'B',
    borderRadius: '50%',
    align: 'center center',
    flexShrink: '0',
    fontSize: 'Z1',
    fontWeight: '700',
    theme: (el, s) => el.scope.themeForRole(s.role),
    text: (el, s) =>
      el.scope.initial(
        s.role === 'user'
          ? el.call('polyglot', 'ai.chat.you')
          : s.role === 'system'
            ? '!'
            : s.role === 'tool'
              ? 'T'
              : 'C'
      )
  },

  Stack: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    flex: '1 1 auto',
    // Assistant has no avatar gutter → take the full row width.
    maxWidth: (el, s) =>
      s.role === 'assistant' || s.role === 'user' ? '100%' : 'calc(100% - 36px)',
    align: (el, s) => (s.role === 'user' ? 'flex-end stretch' : 'flex-start stretch'),

    Meta: {
      // Skip the repeated Author/Time header on grouped continuation rows.
      if: (el, s) => !s._grouped,
      flow: 'x',
      gap: 'Y',
      align: 'center',
      flexDirection: (el, s) => (s.role === 'user' ? 'row-reverse' : 'row'),
      alignSelf: (el, s) => (s.role === 'user' ? 'flex-end' : undefined),
      isAssistantMeta: (el, s) => s.role === 'assistant',
      '.isAssistantMeta': { paddingLeft: 'A' },

      UserAvatar: {
        extends: 'Avatar',
        if: (el, s) => s.role === 'user',
        avatarType: 'initials',
        boxSize: 'B',
        flexShrink: '0',
        src: (el, s) => {
          const u = s.root && s.root.authUser
          return (
            (u && (u.avatar || u.avatarUrl)) || el.call('avatarUrlFor', s.root?.currentUserEmail)
          )
        },
        alt: (el, s) => {
          const u = s.root && s.root.authUser
          void s.root?.lang
          return (u && (u.name || u.email)) || el.call('polyglot', 'ai.chat.you')
        }
      },
      Author: {
        // The author is a property of THE MESSAGE, never of the thread's live
        // routing target. `s.agentName` is stamped at write time (the
        // simoneAiTransport placeholder) and restored from the persisted
        // metadata on a history reload (sessions._toChatMessages), so it is
        // the only agent identity this factory may read. Assistant rows with
        // no agent carry the product AI title, not "Claude".
        // AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1: a `root._targetAgentName`
        // fallback here re-labelled every EARLIER reply the moment the user
        // pressed "Talk to QA". Measured on production 2026-08-30 06:43 — a
        // reply the general assistant wrote redrew its author as `QA`, same
        // id, same timestamp, same body, so the transcript then claimed an
        // author that never wrote it.
        text: (el, s) => {
          void s.root?.lang
          return s.role === 'user'
            ? el.call('polyglot', 'ai.chat.you')
            : s.role === 'system'
              ? el.call('polyglot', 'ai.chat.system')
              : s.role === 'tool'
                ? s.toolName || el.call('polyglot', 'ai.chat.tool')
                : s.agentName || el.call('polyglot', 'ai.chat.assistant')
        },
        fontSize: 'Z1',
        fontWeight: '700',
        color: 'title'
      },
      Time: {
        if: (el, s) => !!s.ts,
        text: (el, s) => {
          const d = new Date(s.ts)
          return el.call('localizedDate', d, { hour: 'numeric', minute: '2-digit' })
        },
        fontSize: 'Z1',
        color: 'caption'
      }
    },

    // Live agentic timeline — the tools this assistant message ran while
    // answering (structured tool.activity frames upserted by the transport;
    // history turns get theirs folded in by sessions._toChatMessages). Rendered
    // ABOVE the answer text, in run order, each row expandable to its exact
    // input/result. Stable keys (tool call id) keep rows from remounting as
    // the array is re-patched during streaming. PERMANENTLY mounted (no `if`) —
    // an if-gated container only mounts when the first tool arrives and gets
    // APPENDED after the answer bubble, putting the steps BELOW the answer;
    // an empty children list renders zero-height, so the always-on container
    // costs nothing and keeps its chronological slot.
    Activity: {
      flow: 'y',
      width: '100%',
      minWidth: '0',
      // ON THE ANSWER'S TEXT EDGE: this inset plus the row's own `Z`
      // (ChatToolRow Head) is the answer block's `A` (Md) — Y + Z = A on the
      // golden scale. `Z` here put the status glyph 4px right of the text.
      paddingLeft: 'Y',
      // A step with the question it asked under it (ChatToolStep, Nika
      // 2026-10-28 "fix positioning"): each row carries the question cards
      // that name its call (`_asks`, always present — a key absent at first
      // render is never tracked), and leaves an answer its card shows to
      // that card (scope.stepRow).
      childExtends: 'ChatToolStep',
      childrenAs: 'state',
      // `s.tools` is read FIRST and unconditionally: behind the role check,
      // an assistant row whose `tools` key was absent at first render never
      // tracked the dependency (short-circuit + absent-key read), so the
      // factory never re-fired and the live timeline stayed empty. The
      // transport also seeds tools:[] on the placeholder — both halves are
      // deliberate (see simoneAiTransport placeholder note). `s.cards` the
      // same way: a question card lands after its step.
      children: (el, s) => {
        const tools = s.tools || []
        const cards = s.cards || []
        if (s.role !== 'assistant') return []
        return tools.map((t) => el.scope.stepRow(t, cards))
      }
    },

    ToolBubble: {
      if: (el, s) => s.role === 'tool',
      extends: 'ToolCallCard'
    },

    Bubble: {
      if: (el, s) => s.role !== 'tool',
      alignSelf: (el, s) => (s.role === 'user' ? 'flex-end' : 'flex-start'),
      maxWidth: (el, s) => (s.role === 'assistant' ? '100%' : '85%'),
      borderRadius: 'B B Y B',
      // Padding MUST be a static token value. A reactive
      // `padding: (el,s) => …'Z A'` is a spacing prop with a function value,
      // which this bundle's DOMQL drops entirely — that's why every bubble
      // rendered with padding:0 ("bubbles lost padding"). Base = the
      // user/system padded bubble; the flush full-width assistant variant
      // lives in the static `.isAssistant` modifier so its tokens resolve.
      padding: 'Z A',
      isAssistant: (el, s) => s.role === 'assistant',
      isUser: (el, s) => s.role === 'user',
      // Live-answer feel: while THIS message is the one actively streaming
      // (s.streaming, flipped false server-side when the turn ends — see
      // simoneAiTransport.js `_patchAssistantMessage(..., { streaming: false })`)
      // its tail fades in via a masked gradient on the bottom edge (~last
      // fifth of the block). The mask is recomputed by the browser on every
      // layout pass as the text grows, so newly arriving lines materialize
      // softly with NO per-delta JS or re-render loop. History rows (and the
      // other party's messages) never carry `streaming`, so they never
      // re-trigger this — only the live answer breathes.
      isStreaming: (el, s) => s.role === 'assistant' && !!s.streaming && !!s.text,
      '.isAssistant': { padding: 'Y2 0' },
      '.isUser': { borderRadius: 'B B Y B' },
      // Standard maskImage only — Chrome unprefixed since 120; a webkit* camel
      // key would be silently dropped by css-in-props (not a registered prop).
      '.isStreaming': {
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,1) 20%)'
      },
      // borderRadius: 'B',
      fontSize: 'A',
      lineHeight: '1.5',
      wordBreak: 'normal',
      overflowWrap: 'anywhere',
      // Assistant = flush/transparent. User (sent) = a solid accent bubble that
      // reads in dark mode — the old 'primary' theme rendered a WHITE bubble in
      // dark mode (white blob + near-black text). System = warning.
      theme: (el, s) => (s.role === 'system' ? 'warning' : 'transparent'),
      background: (el, s) => (s.role === 'user' ? 'blue' : undefined),
      color: (el, s) => (s.role === 'user' ? 'fillInk' : undefined),

      // Assistant text → rendered as MARKDOWN (bold, lists, code, links,
      // headings) via a `children:` factory (markdownToChildren). NOT
      // el.set/ChatMarkdown: el.set inside this reactive state-driven message
      // list re-fires the parent and wipes the subtree in v3.14 (see marketplace
      // MarketplaceDetailMarkdownBody) — that's why answers came back INVISIBLE.
      // A children spec diff-renders cleanly.
      Md: {
        if: (el, s) => s.role === 'assistant' && !!s.text,
        flow: 'y',
        gap: 'Z',
        minWidth: '0',
        paddingLeft: 'A',
        '> ul': {
          listStylePosition: 'inside',
          paddingLeft: '0',
          margin: '0'
        },
        '> ol': {
          listStylePosition: 'inside',
          paddingLeft: '0',
          margin: '0'
        },
        // ── Markdown element polish (2026-07-25 per Nika "improve md
        // converting into domql looking better / clickable links / fully
        // interactive") — design-token styling for every element the
        // symbolize transpiler emits, so an assistant answer reads as
        // designed UI rather than raw browser defaults. Descendant rules
        // (not per-node props) so BOTH renderer paths — symbolize and the
        // brender-safe inline fallback — pick them up identically.
        '& table': {
          display: 'block',
          width: '100%',
          overflowX: 'auto',
          borderCollapse: 'collapse',
          margin: 'Y 0 Z',
          fontSize: 'Z1'
        },
        '& th': {
          textAlign: 'left',
          padding: 'Y Z1 Y 0',
          borderBottom: '1px solid var(--color-line)',
          color: 'var(--color-title)',
          fontWeight: '700',
          whiteSpace: 'nowrap'
        },
        '& td': {
          padding: 'Y Z1 Y 0',
          borderBottom: '1px solid var(--color-line)',
          color: 'var(--color-paragraph)',
          verticalAlign: 'top'
        },
        '& tbody tr:last-child td': { borderBottom: 'none' },
        '& a': {
          color: 'var(--color-blue)',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          cursor: 'pointer',
          transition: 'A opacity defaultBezier'
        },
        '& a:hover': { opacity: '.8' },
        '& pre': {
          background: 'var(--color-line)',
          padding: 'Z A',
          borderRadius: 'var(--spacing-Z)',
          overflowX: 'auto',
          fontSize: 'Z1',
          lineHeight: '1.5',
          margin: 'Y 0'
        },
        '& code': {
          background: 'var(--color-line)',
          padding: 'V Y',
          borderRadius: 'var(--spacing-Y)',
          fontSize: '.92em'
        },
        '& pre code': { background: 'transparent', padding: '0' },
        '& blockquote': {
          borderLeft: '2px solid var(--color-line)',
          paddingLeft: 'A',
          color: 'var(--color-caption)',
          margin: 'Y 0'
        },
        '& hr': {
          border: 'none',
          borderTop: '1px solid var(--color-line)',
          margin: 'A 0'
        },
        '& li': { margin: '0 0 var(--spacing-Y2)' },
        '& p': { margin: '0' },

        // ── Streaming render (2026-08-01, per Nika "streaming animation,
        // texts flickering, replacing loading") ─────────────────────────
        // STABLE NAMED children instead of a per-delta `children` factory:
        // the old factory returned a FRESH [text, caret] array on every SSE
        // delta, so DOMQL re-created both nodes each chunk — the caret's
        // blink animation restarted (reads as flicker) and the text node
        // remounted instead of mutating. Named children reconcile by key:
        // `StreamText`'s `text` factory re-evaluates in place (a plain
        // textContent write), the caret ELEMENT persists across the whole
        // turn, and the thinking Dots below hide in the same state pass the
        // first token lands — loading is REPLACED by the stream, no gap.
        Stream: {
          if: (el, s) => s.role === 'assistant' && !!s.streaming,
          whiteSpace: 'pre-wrap',
          // Soft entrance for the first tokens (brand `fadeIn` keyframes) —
          // plays once when the stream block mounts, not per delta.
          animationName: 'fadeIn',
          animationDuration: '180ms',
          animationTimingFunction: 'ease-out',

          StreamText: {
            tag: 'span',
            whiteSpace: 'pre-wrap',
            text: (el, s) => s.text || ''
          },
          StreamCaret: {
            tag: 'span',
            display: 'inline-block',
            width: '0.5em',
            height: '1.05em',
            marginLeft: 'V',
            verticalAlign: 'text-bottom',
            background: 'title',
            animationName: 'blinking',
            animationDuration: '800ms',
            animationIterationCount: 'infinite'
          }
        },

        // ── Final render — markdown parsed ONCE when the turn ends ──────
        // (parsing the full GROWING markdown per delta is O(n²) over a turn,
        // and half-finished markdown — mid-token `**`, unclosed fences —
        // renders as artifacts). The `if:` swap from Stream to Rendered is
        // the single intentional repaint of the turn; the fadeIn makes it a
        // designed settle instead of a flash. `>` heading rules ride here
        // because the markdown tree's top level is now one child deeper.
        Rendered: {
          if: (el, s) => s.role === 'assistant' && !s.streaming && !!s.text,
          flow: 'y',
          gap: 'Z',
          minWidth: '0',
          animationName: 'fadeIn',
          animationDuration: '180ms',
          animationTimingFunction: 'ease-out',
          '> h1': { fontSize: 'B', lineHeight: '1.25', margin: 'A 0 Z' },
          '> h2': { fontSize: 'B', lineHeight: '1.25', margin: 'A 0 Z' },
          '> h3': { fontSize: 'A1', lineHeight: '1.3', margin: 'Z 0 Y' },
          '> h4': { fontSize: 'A1', lineHeight: '1.3', margin: 'Z 0 Y' },
          '> h5': { fontSize: 'A', lineHeight: '1.35', margin: 'Y 0 V' },
          '> h6': { fontSize: 'A', lineHeight: '1.35', margin: 'Y 0 V' },
          // Markdown → children via the SHARED, registered
          // `renderAssistantMarkdown` (symbolize-markdown). el.call is a
          // runtime string lookup, so it survives frank serialization on the
          // brender (docs.symbols.app) — a bare module-ref to
          // markdownToChildren / a components map inside this serialized
          // factory throws there (frank doesn't rewrite free vars in hoisted
          // helpers — see tickets/smbls.md). docs, preview and workspace all
          // share one renderer.
          //
          // AIUX-CHROME item 4 — a synthesized-error row's `s.text` is the
          // server's raw diagnostic (see `el.scope.humanErrorText` above);
          // swap it for human copy before it ever reaches the renderer. Only
          // credits-exhaustion codes are exempt (humanErrorText returns null
          // for those, so they keep their own server text + banner), and so is
          // a `memberText` row (its text is written for the member).
          children: (el, s) => {
            const human =
              s.metadata && s.metadata.kind === 'error'
                ? el.scope.humanErrorText(s.metadata, s.tools, s.text)
                : null
            // AI-302 — the render-time protocol gate (el.scope
            // .protocolLeakNotice): tool-call wire text never renders as the
            // answer in ANY host. Checked on the text that would actually
            // render; a human error override is already safe copy.
            const leak = human ? null : el.scope.protocolLeakNotice(s.text)
            return el.call('renderAssistantMarkdown', human || leak || s.text || '')
          }
        }
      },
      // User / system messages render as plain text, preserving newlines.
      Plain: {
        if: (el, s) => s.role !== 'assistant',
        whiteSpace: 'pre-wrap',
        text: (el, s) => s.text || ''
      },
      // Streaming placeholder before the first delta lands — a quiet
      // "Thinking…" status line ("Working…" while a tool row is live above it),
      // with the pulsing dots carrying the motion.
      Dots: {
        if: (el, s) => s.role === 'assistant' && !s.text && !!s.streaming,
        flow: 'x',
        gap: 'Y',
        align: 'center',
        padding: 'Y 0 Y A',
        PvThinkingLabel: {
          tag: 'span',
          fontSize: 'Z1',
          color: 'caption',
          fontWeight: '500',
          animationName: 'blinking',
          animationDuration: '1600ms',
          animationTimingFunction: 'ease-in-out',
          animationIterationCount: 'infinite',
          text: (el, s) => {
            void s.root?.lang
            const tools = s.tools || []
            // A step that waits for the person's OK is not "Thinking" — the
            // same words as the island's head (one label, ai.chat.needsOk).
            if (tools.some((t) => t && t.status === 'approval')) {
              return el.call('polyglot', 'ai.chat.needsOk')
            }
            return tools.some((t) => t && t.status === 'running')
              ? el.call('polyglot', 'ai.chat.working')
              : el.call('polyglot', 'ai.chat.thinking')
          }
        },
        // AI-305 (tickets/ai.md) — a LONG run must show motion beyond the
        // caret: count the completed tool rows live. Reads the same `s.tools`
        // the timeline above renders, so the number can never disagree with
        // the visible rows (the acceptance: counter === done rows). Empty
        // while nothing has completed — the label alone carries the idle
        // state, no "0 actions" noise. Steady (no blink): completed work is
        // a fact, not an in-flight signal.
        PvDoneCount: {
          tag: 'span',
          fontSize: 'Z1',
          color: 'caption',
          opacity: '.75',
          hide: (el, s) => !(s.tools || []).some((t) => t && t.status === 'done'),
          text: (el, s) => {
            void s.root?.lang
            const n = (s.tools || []).filter((t) => t && t.status === 'done').length
            if (!n) return ''
            return el
              .call('polyglot', n === 1 ? 'ai.chat.actionDone' : 'ai.chat.actionsDone')
              .replace('{count}', String(n))
          }
        },
        PvDots: {
          flow: 'x',
          gap: 'X',
          align: 'center',
          childExtends: {
            boxSize: 'X2',
            round: '50%',
            background: 'caption',
            flexShrink: '0',
            animationName: 'blinking',
            animationDuration: '1100ms',
            animationTimingFunction: 'ease-in-out',
            animationIterationCount: 'infinite'
          },
          PvDot_1: {},
          PvDot_2: { animationDelay: '180ms' },
          PvDot_3: { animationDelay: '360ms' }
        }
      },

      // S1 (NIKA BATCH 2026-08-16) — token count + elapsed time for the
      // whole run, not just the pre-first-token wait (unlike Dots above,
      // which hides once `s.text` starts filling in). `Dots`/`Stream`/`Md`/
      // `Rendered` are siblings directly under `Bubble`, EACH gated by its
      // own `if:` — there's no shared wrapper to inherit a gate from
      // (verified via a live import + object-path walk, not assumed from
      // the visual indentation, which is inconsistent in this file).
      // `hide:`, never `if:`, so the interval-driven ticking node isn't
      // destroyed and recreated every time `s.streaming` flips. Both cells
      // reserve worst-case width — `9 → 10 → 100` must never reflow the
      // row, the ticket's own words for "worse than no counter".
      PvUsage: {
        tag: 'span',
        hide: (el, s) => s.role !== 'assistant' || !s.streaming,
        flow: 'x',
        gap: 'V',
        align: 'center',
        padding: 'V 0 0 A',
        fontSize: 'Z1',
        color: 'caption',
        opacity: '.6',

        PvElapsed: {
          tag: 'span',
          display: 'inline-block',
          minWidth: 'C',
          // Left in its reserved cell: the line starts on the answer's text
          // edge (right-aligned, "0s" began 22px in), and the reserved width
          // still keeps 9 → 10 → 100 from reflowing the row.
          textAlign: 'left',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          // `_tick` has no meaning of its own — simoneAiTransport.js's
          // elapsed-timer interval touches it once a second purely to give
          // this factory a state key to react to on pure wall-clock
          // passage (no delta, no tool event fires otherwise).
          text: (el, s) => {
            void s._tick
            const secs = Math.max(0, Math.floor((Date.now() - (s.ts || Date.now())) / 1000))
            return `${secs}s`
          }
        },
        PvUsageDot: { tag: 'span', text: '·' },
        PvTokens: {
          tag: 'span',
          display: 'inline-block',
          minWidth: 'C',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          // While streaming: a rough, explicitly-labeled ESTIMATE from the
          // growing text (~4 chars/token, a common rough heuristic — never
          // presented as exact). Once `s.usage` lands (onDone), the row
          // shows the server's real, authoritative count instead — the
          // "settles at a final value" half of the acceptance.
          text: (el, s) => {
            if (s.usage && typeof s.usage.completionTokens === 'number') {
              const total = (s.usage.promptTokens || 0) + s.usage.completionTokens
              return `${total} tokens`
            }
            const estimate = Math.round((s.text || '').length / 4)
            return estimate ? `~${estimate} tokens` : ''
          }
        }
      }
    },

    // Picked-element refs the user attached when they sent this message.
    // Rendered as small chips BELOW the user bubble so they stay visible
    // when the message list auto-scrolls to the bottom.
    // O1 (opus.md): the picked-elements echo on a sent user message renders
    // with the ONE chip (read-only: no removeVia), same grammar as the composer's
    // AiContextBar — the fourth near-copy of this row is gone.
    PickedAttached: {
      if: (el, s) => s.role === 'user' && !!(s.picked && s.picked.length),
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Z',
      alignSelf: 'flex-end',
      justifyContent: 'flex-end',
      maxWidth: '85%',
      childExtends: 'AiContextChip',
      childrenAs: 'state',
      children: (el, s) =>
        (s.picked || []).map((p, i) => {
          const kp = Array.isArray(p.keyPath) ? p.keyPath : []
          // An inspected region (the composer's Inspect & ask) went with the
          // ask as structured facts — its chip says so (pageContext.js's
          // composer chip reads the same way).
          const region = !!(p.inspect && typeof p.inspect === 'object')
          return {
            key: 'picked-' + (p.pickedAt != null ? p.pickedAt : i),
            id: 'picked:' + i,
            kind: region ? 'region' : 'element',
            title: kp.length
              ? kp.join(' › ')
              : p.key || (p.tagPath || []).slice(-2).join(' > ') || p.tag || '(element)',
            icon: region ? 'lnInspect' : 'cursor',
            source: 'picked',
            sent: region ? 'structured' : 'text-only',
            removeVia: null,
            tier: 'attached'
          }
        })
    },

    // ── SOURCES (spec §4.2) ─────────────────────────────────────────────
    // The entities this answer actually cited, as chips under the reply.
    // Each entry is `{ ref, type, id, title, use, verified }` — the server's
    // persisted shape; `use` is the model's own one-line reason ("warranty
    // cap, cl. 9"), and `verified: false` marks a ref the turn's retrieval
    // never actually read (it is KEPT, not hidden — a claimed source the
    // tools did not open is information, and silently dropping it would make
    // an unverified answer look identical to a grounded one).
    //
    // The chip is composed INLINE rather than reusing AiContextChip: that
    // component's click is bound to the alterable-reference mute toggle and
    // its title factory renders the mute sentence, so a navigating chip with
    // a `use` caption would be fighting it. Same call the Suggestions row
    // below makes — an inline `childExtends`, not a fifth exported near-copy.
    Sources: {
      if: (el, s) =>
        s.role === 'assistant' && !s.streaming && Array.isArray(s.sources) && s.sources.length > 0,
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Y',
      width: '100%',
      minWidth: '0',
      marginTop: 'Z',
      paddingLeft: 'A',
      childrenAs: 'state',
      // A source may arrive as the bare string form or as the full row; both
      // normalize to a state bag the chip reads. `key` keeps rows stable
      // across the onDone patch that fills them in.
      children: (el, s) =>
        (s.sources || [])
          .filter(Boolean)
          .map((x, i) =>
            typeof x === 'string' ? { key: 'src-' + i, ref: x } : { key: 'src-' + i, ...x }
          ),
      childExtends: 'ChatSourceChip'
    },

    // ── ADVICE LEVEL (vault spec 06 §7) ─────────────────────────────────
    // Only on a settled assistant row whose level bag took a position — a
    // workspace that does not grade its answers never renders this block.
    // It sits above Confidence, in the order the VaultOS answer sheet uses.
    // The pill takes the confidence pill's plate and metrics so the two read
    // as one family, but no tone: the level is not a second confidence. A
    // click discloses the requested and emitted levels, every ceiling that
    // applied and the retreat (06 §7). Expansion rides `root._aiLevelOpen`
    // keyed by message id — the confidence pill's own shape.
    Level: {
      if: (el, s) => s.role === 'assistant' && !s.streaming && !!el.scope.levelView(s.level),
      flow: 'y',
      gap: 'Y',
      width: '100%',
      minWidth: '0',
      marginTop: 'Y',
      paddingLeft: 'A',
      color: 'caption',

      Pill: {
        tag: 'button',
        type: 'button',
        alignSelf: 'flex-start',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        padding: 'X Z',
        round: 'radiusPill',
        background: 'overlay',
        borderWidth: '0',
        cursor: 'pointer',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'title',
        transition: 'B defaultBezier background',
        ':hover': { background: 'hover' },
        aria: {
          expanded: (el, s) => !!(s.root && s.root._aiLevelOpen && s.root._aiLevelOpen[s.id]),
          label: (el) => el.call('polyglot', 'ai.level.toggle')
        },
        Label: {
          tag: 'span',
          text: (el, s) => {
            const v = el.scope.levelView(s.level)
            return v ? el.call('polyglot', v.labelKey) : ''
          }
        },
        Value: {
          tag: 'span',
          if: (el, s) => !!(el.scope.levelView(s.level) || {}).value,
          text: (el, s) => (el.scope.levelView(s.level) || {}).value || '',
          color: 'caption',
          fontWeight: '400'
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          const root = s.root
          if (!root || !root.update) return
          const open = { ...(root._aiLevelOpen || {}) }
          open[s.id] = !open[s.id]
          // preventFetch — disclosure chrome, not a data read.
          root.update({ _aiLevelOpen: open }, { preventFetch: true })
        }
      },

      // `hide:` not `if:` — the Breakdown note below says why. The root key
      // is read behind a guard, so it is declared as a dependency (FA515).
      Detail: {
        hide: (el, s) => !(s.root && s.root._aiLevelOpen && s.root._aiLevelOpen[s.id]),
        hideDeps: [(el, s) => s.root && s.root._aiLevelOpen],
        flow: 'y',
        gap: 'X',
        width: '100%',
        minWidth: '0',
        padding: 'Z A2',
        round: 'radiusControl',
        background: 'overlay',
        Levels: {
          tag: 'span',
          fontSize: 'X',
          color: 'caption',
          text: (el, s) => {
            const v = el.scope.levelView(s.level)
            if (!v) return ''
            const asked =
              v.requested === null
                ? ''
                : el.call('polyglot', 'ai.level.requested') + ' ' + v.requested
            const given =
              v.emitted === null ? '' : el.call('polyglot', 'ai.level.emitted') + ' ' + v.emitted
            return [asked, given].filter(Boolean).join(' · ')
          }
        },
        Ceilings: {
          if: (el, s) => ((el.scope.levelView(s.level) || {}).ceilings || []).length > 0,
          flow: 'y',
          gap: 'X',
          width: '100%',
          minWidth: '0',
          CeilingsTitle: {
            tag: 'span',
            fontSize: 'X',
            fontWeight: '700',
            color: 'caption',
            text: (el) => el.call('polyglot', 'ai.level.ceilingsTitle')
          },
          // Each ceiling is the sentence the server phrased — never re-worded.
          CeilingsRows: {
            flow: 'y',
            gap: 'X',
            children: (el, s) =>
              ((el.scope.levelView(s.level) || {}).ceilings || []).map((c) => ({
                key: c.key,
                tag: 'span',
                fontSize: 'X',
                color: 'caption',
                text: c.text
              }))
          }
        },
        // A retreat is announced, never silently applied (02 §6).
        Retreat: {
          tag: 'p',
          if: (el, s) => !!(el.scope.levelView(s.level) || {}).retreat,
          margin: '0',
          fontSize: 'X',
          color: 'caption',
          text: (el, s) =>
            el.call('polyglot', 'ai.level.retreat') +
            ': ' +
            (el.scope.levelView(s.level) || {}).retreat
        }
      }
    },

    // ── CONFIDENCE (spec §4.3) ──────────────────────────────────────────
    // On a SCORED assistant row in a workspace that grounds its answers (the
    // gate on `if:` below). Before server c9b2ea74 every answer carried the
    // pill and an unscored one read the muted "Unscored" — the server now
    // scores only where grounding is on, so "Unscored" everywhere else said
    // nothing true; a row with no verdict shows no pill. The muted label
    // stays for a verdict whose band cannot be read.
    //
    // The pill is the whole affordance; clicking it discloses the six
    // weighted factors, the caps that were applied and the one-line reason.
    // Expansion rides `root._aiConfidenceOpen` keyed by message id — the same
    // shape ChatToolRow uses for its own rows, so a reload or a thread switch
    // starts every row collapsed and two rows never share one flag.
    Confidence: {
      // The label shows only where the workspace grounds its answers — server
      // settings.ai.grounding, or a workspace that runs the advice gradient
      // (settings.gradientCeiling). The server writes a verdict only there
      // (server c9b2ea74, 2026-09-28), so a row without a SCORE shows NO pill
      // (never "Unscored"), and the workspace check also hides the verdicts
      // older rows carry from before that change. (core-wl's patch, with the
      // score read through confBandKey — an empty verdict `{}` is no score.)
      if: (el, s) => {
        if (s.role !== 'assistant' || s.streaming) return false
        if (!el.scope.confBandKey(s.confidence)) return false
        // A capabilities answer (describe_capabilities / the composer's "What
        // can you do here?") is the workspace's own catalogue of what is
        // installed — deterministic, nothing to ground — and the verdict the
        // turn carries is about missing sources, not about it. Measured on
        // dev 2026-10-05: "Insufficient 0%" under the Tokens catalogue card.
        if ((Array.isArray(s.cards) ? s.cards : []).some((c) => c && c.type === 'capabilities'))
          return false
        const ws = s.root && s.root.workspace
        const settings = (ws && ws.settings) || {}
        return (
          (settings.ai && settings.ai.grounding === true) ||
          (settings.gradientCeiling !== undefined && settings.gradientCeiling !== null)
        )
      },
      flow: 'y',
      gap: 'Y',
      width: '100%',
      minWidth: '0',
      marginTop: 'Y',
      paddingLeft: 'A',
      // The tone is declared ONCE, here, and both readings inherit it through
      // `currentColor`: the pill's label + dot, and the breakdown's bar fills.
      // Everything else in the block sets `caption` explicitly, so the tint
      // never leaks into body copy.
      color: (el, s) => el.scope.confTone(s.confidence),

      Pill: {
        tag: 'button',
        type: 'button',
        alignSelf: 'flex-start',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        padding: 'X Z',
        round: 'radiusPill',
        background: 'overlay',
        borderWidth: '0',
        cursor: 'pointer',
        fontSize: 'Y2',
        fontWeight: '700',
        // No `color` of its own: it inherits the container's tone above, and
        // the plate stays the neutral house wash so no hue FILL enters the
        // chrome register (design-direction §6/§8).
        transition: 'B defaultBezier background',
        ':hover': { background: 'hover' },
        // A finger's target: the pill is 28px tall (measured on dev DFI) —
        // the 44pt floor (_touchTarget.js).
        '@coarse': { position: 'relative', ...touchFloor },
        aria: {
          expanded: (el, s) =>
            !!(s.root && s.root._aiConfidenceOpen && s.root._aiConfidenceOpen[s.id]),
          label: (el) => el.call('polyglot', 'ai.confidence.toggle')
        },
        Dot: {
          boxSize: 'Y',
          round: '50%',
          background: 'currentColor',
          flexShrink: '0'
        },
        Label: {
          tag: 'span',
          text: (el, s) => {
            const key = el.scope.confBandKey(s.confidence)
            if (!key) return el.call('polyglot', 'ai.confidence.unscored')
            // The server may send its OWN band label (spec §4.3 `bandLabel`);
            // it wins, because it is the phrasing the score was computed with.
            const stated = s.confidence && s.confidence.bandLabel
            return stated ? String(stated) : el.call('polyglot', key)
          }
        },
        Score: {
          tag: 'span',
          if: (el, s) => !!el.scope.confPercent(s.confidence),
          text: (el, s) => el.scope.confPercent(s.confidence),
          color: 'caption',
          fontWeight: '400'
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          const root = s.root
          if (!root || !root.update) return
          const open = { ...(root._aiConfidenceOpen || {}) }
          open[s.id] = !open[s.id]
          // preventFetch — this is disclosure chrome, not a data read (the
          // same call ChatToolRow's row toggle makes).
          root.update({ _aiConfidenceOpen: open }, { preventFetch: true })
        }
      },

      // The breakdown. `hide:` not `if:` — an if-gated child REMOUNTS on every
      // flip and lands appended at the container's end (the trap ChatToolRow's
      // PvStatus note records); a hidden child keeps its slot.
      Breakdown: {
        hide: (el, s) => !(s.root && s.root._aiConfidenceOpen && s.root._aiConfidenceOpen[s.id]),
        flow: 'y',
        gap: 'Y',
        width: '100%',
        minWidth: '0',
        padding: 'Z A2',
        round: 'radiusControl',
        background: 'overlay',

        // The six factors, as compact bars. Rows share one fontSize so their
        // labels and figures sit on one baseline (Rule 34).
        Factors: {
          flow: 'y',
          gap: 'Y',
          width: '100%',
          minWidth: '0',
          childrenAs: 'state',
          // `Factors` declares no state of its own, so `s` IS the message
          // state it inherits — no `s.parent` hop (that would reach the
          // Breakdown element's state, which is the same object anyway and
          // reads as if a hop were needed).
          children: (el, s) => el.scope.confRows(s.confidence),
          childExtends: 'ChatConfidenceFactor'
        },

        // The caps that clamped the score (spec §4.3: no sources → ≤ .29, one
        // source → ≤ .54, …). Each is a plain sentence the server already
        // phrased — never re-worded here.
        Caps: {
          if: (el, s) => Array.isArray(s.confidence?.caps) && s.confidence.caps.length > 0,
          flow: 'y',
          gap: 'X',
          width: '100%',
          minWidth: '0',
          marginTop: 'Y',
          CapsTitle: {
            tag: 'span',
            text: '{{ ai.confidence.capsTitle | polyglot }}',
            fontSize: 'X',
            fontWeight: '700',
            color: 'caption'
          },
          // Plain children (element objects), not childrenAs+childExtends: a
          // cap is one static sentence with no interaction and no reactive
          // prop of its own, so a registered child component would be a name
          // with nothing behind it (FA808 is about SHAPES worth naming).
          CapsRows: {
            flow: 'y',
            gap: 'X',
            children: (el, s) =>
              ((s.confidence && s.confidence.caps) || []).map((c, i) => ({
                key: 'cap-' + i,
                tag: 'span',
                fontSize: 'X',
                color: 'caption',
                text: typeof c === 'string' ? c : String((c && (c.text || c.reason)) || '')
              }))
          }
        },

        // The one-line why. Last, because it reads as the summary of the rows
        // above it.
        Reason: {
          tag: 'p',
          if: (el, s) => !!s.confidence?.reason,
          margin: '0',
          marginTop: 'Y',
          fontSize: 'X',
          color: 'caption',
          text: (el, s) => String(s.confidence.reason)
        },

        // No score at all — say so inside the disclosure too, so an opened
        // "Unscored" pill is not an empty box.
        NoScore: {
          tag: 'p',
          if: (el, s) => !el.scope.confBandKey(s.confidence),
          margin: '0',
          fontSize: 'X',
          color: 'caption',
          text: '{{ ai.confidence.noScore | polyglot }}'
        }
      }
    },

    // The message's cards — all but a question its own step holds
    // (ChatToolStep). ON THE ANSWER'S TEXT EDGE, the Md block's `A` (Nika
    // 2026-10-28 "fix positioning"): the card box started on the message's
    // own edge, A (16–17px) left of the text above it in every host —
    // measured on dev at 1920 / 1280, in the drawer and at 390.
    Cards: {
      if: (el, s) => el.scope.looseCards(s).length > 0,
      flow: 'y',
      gap: 'Z',
      width: '100%',
      minWidth: '0',
      marginTop: 'Y',
      paddingLeft: 'A',
      childExtends: 'ChatCard',
      childrenAs: 'state',
      children: (el, s) => el.scope.looseCards(s)
    },

    // AI follow-up suggestions — the assistant's next-step CTAs, attached to
    // this reply (server-validated `metadata.suggestions`, grounded in the
    // workspace's apps + capabilities). Rendered on EVERY completed assistant
    // message that carries them (2026-08-12 per Nika "always followup with
    // beautiful buttons as call to actions from each message"), never while
    // the reply is still streaming; the newest row (`_last`, stamped in
    // Chat.js's Messages children map) reads at full strength while older
    // rows step back and wake on approach. Tapping one SENDS its prompt
    // immediately as the user's next message through the normal send path —
    // same-scope sendPrompt, so the drawer / dashboard / add-app instance
    // each route to their own thread and the AI continues automatically.
    // Solid `layer` capsules — fill-contrast, borderless (design-direction
    // §8), house `hover` wash + lift on approach (chrome register:
    // monochrome interaction, no hue fills). Distinct from ask_user choice
    // buttons (a required mid-task answer): these are OPTIONAL follow-ups.
    Suggestions: {
      if: (el, s) =>
        s.role === 'assistant' &&
        !s.streaming &&
        Array.isArray(s.suggestions) &&
        s.suggestions.length > 0,
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Y',
      width: '100%',
      minWidth: '0',
      marginTop: 'Z',
      paddingLeft: 'A',
      // History rows keep their CTAs reachable but stepped back (static
      // opacity step — zero layout shift; the chips stay fully interactive
      // and carry their own hover feedback).
      opacity: (el, s) => (s._last ? '1' : '.7'),
      transition: 'B opacity defaultBezier',
      animationName: 'fadeInUpShort',
      animationDuration: '420ms',
      animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
      childrenAs: 'state',
      children: (el, s) => (s.suggestions || []).map((x, i) => ({ key: 'sg-' + i, ...x })),
      childExtends: {
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        padding: 'Y Z1',
        minHeight: 'controlHCompact',
        round: 'radiusPill',
        // A finger's target: a 36px chip takes the house tap slop (_touchTarget.js).
        '@coarse': { position: 'relative', ...touchSlop },
        // Solid elevated chip (the Starters' exact surface) — fill-contrast
        // separation, no hairline ring (design-direction §8); 'overlay' is a
        // 5% wash that reads as bare text on the document ground.
        theme: 'layer',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        border: 'none',
        maxWidth: '100%',
        minWidth: '0',
        textAlign: 'left',
        transition: 'B defaultBezier',
        transitionProperty: 'background-color, color, transform',
        // Hover = the house fill step + a small lift (chrome register:
        // monochrome interaction — hue never marks hover; the wash rides on
        // top of the layer chip).
        ':hover': {
          background: 'hover',
          transform: 'translateY(-1px)'
        },
        ':active': { transform: 'translateY(0)' },
        // Nudge the arrow toward the action on hover — the chip reads as
        // "continue with this".
        ':hover > svg': { transform: 'translateX(2px)' },
        // The follow-up's own words — the chip's primary text: it wraps
        // inside the capsule instead of being cut (the overflow law, §3b
        // rule 8; the chip already caps at the message's width).
        Label: {
          tag: 'span',
          text: (el, s) => s.label,
          minWidth: '0',
          overflowWrap: 'anywhere'
        },
        // No explicit `color` — the glyph rides the button ink via
        // currentColor, so it flips to white with the hover fill.
        Icon: {
          name: 'arrowRight',
          fontSize: 'Z1',
          flexShrink: '0',
          transition: 'B transform defaultBezier'
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          const prompt = s.prompt || s.label
          if (!prompt) return
          // Resolve this Chat instance's scope by walking up for `chatScope`
          // — inlined (no helper import) so the walk survives frank
          // serialization, same idiom as Chat.js `ck`.
          let scope = 'general'
          let n = el
          while (n) {
            if (n.chatScope) {
              scope = n.chatScope
              break
            }
            n = n.parent
          }
          try {
            // THE ASK ROUTER (workspace functions/assistantAskRoute.js
            // assistantAskHandoff): a chip that clearly asks for another
            // surface — a page, an app, slides, a website, the tour — goes
            // there; every other chip continues this thread. Only the general
            // thread routes (the builder's own chat never does).
            const fns = el.context && el.context.functions
            if (
              scope === 'general' &&
              fns &&
              typeof fns.assistantAskHandoff === 'function' &&
              el.call('assistantAskHandoff', prompt, { from: 'chip' })
            ) {
              return
            }
            el.call('sendPrompt', prompt, scope)
          } catch (_) {
            /* transport not ready — chip is a no-op rather than a throw */
          }
        }
      }
    },

    // AIUX-CHROME item 4 (tickets/sonnet.md) — "Rewrite [error copy] to
    // human copy + a retry / switch-model affordance." A synthesized-error
    // row (el.scope.isRetryableError — non-credits `metadata.kind:'error'`,
    // `retryable !== false`) gets a Retry button (re-sends the immediately
    // preceding user prompt through the normal send path — same mechanism
    // Suggestions above uses) and a Switch model button (opens the shared
    // model-switcher dropdown via the same `root._modelMenuOpen` flag
    // AiModelSwitcherTrigger uses — shared/components/AiModelSwitcher.js).
    // Credits-exhaustion errors are excluded — those already get a dedicated
    // banner + admin CTA (notifyCreditsExhaustedFromMessage) where a bare
    // "Retry" would just fail again.
    ErrorActions: {
      // AI-304 — the row shows for BOTH synthesized-error affordance sets:
      // retryable (no tool ran — Retry re-sends the prompt) and continuable
      // (a tool already completed — Continue resumes; Retry stays hidden so
      // the completed write is never re-run by reflex).
      if: (el, s) => el.scope.isRetryableError(s) || el.scope.isContinuableError(s),
      flow: 'x',
      gap: 'Y',
      width: '100%',
      marginTop: 'Z',
      paddingLeft: 'A',
      // AI-304 — one-click resume for a failed-after-tools turn. Sends the
      // member's Continue word with a resume flag through the same sendPrompt
      // path the sibling buttons use; the conversation's persisted tool results give the model
      // its state, and AI-202's server-side idempotency replay makes an
      // accidentally re-issued identical write return the ORIGINAL result
      // instead of a duplicate row. Same chip anatomy as RetryBtn (house
      // layer pill).
      ContinueBtn: {
        hide: (el, s) => !el.scope.isContinuableError(s),
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        padding: 'Y Z1',
        minHeight: 'controlHCompact',
        round: 'radiusPill',
        theme: 'layer',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        border: 'none',
        transition: 'B defaultBezier',
        transitionProperty: 'background-color, color, transform',
        ':hover': { background: 'hover', transform: 'translateY(-1px)' },
        ':active': { transform: 'translateY(0)' },
        Icon: { name: 'arrowRight', fontSize: 'Z1', flexShrink: '0' },
        Label: { tag: 'span', text: '{{ common.action.continue | polyglot }}' },
        onClick: (e, el, s) => {
          e.preventDefault()
          // Resolve this Chat instance's scope — inlined (frank-safe), same
          // idiom as the sibling buttons.
          let scope = 'general'
          let n = el
          while (n) {
            if (n.chatScope) {
              scope = n.chatScope
              break
            }
            n = n.parent
          }
          // The member's bubble says the chip's own word, in the member's
          // language (persona round 4, elena: the model's resume instruction
          // showed under "You"). The resume itself rides `opts.resume` →
          // `context.resume`; the server tells the model to pick up without
          // redoing the saved actions.
          try {
            el.call(
              'sendPrompt',
              el.call('polyglot', 'common.action.continue'),
              scope,
              null,
              null,
              { resume: true }
            )
          } catch (_) {
            /* transport not ready — chip is a no-op rather than a throw */
          }
        }
      },
      RetryBtn: {
        hide: (el, s) => el.scope.isContinuableError(s),
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        padding: 'Y Z1',
        minHeight: 'controlHCompact',
        round: 'radiusPill',
        theme: 'layer',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        border: 'none',
        transition: 'B defaultBezier',
        transitionProperty: 'background-color, color, transform',
        ':hover': { background: 'hover', transform: 'translateY(-1px)' },
        ':active': { transform: 'translateY(0)' },
        Icon: { name: 'refresh', fontSize: 'Z1', flexShrink: '0' },
        Label: { tag: 'span', text: '{{ common.action.try_again | polyglot }}' },
        onClick: (e, el, s) => {
          e.preventDefault()
          // Resolve this Chat instance's scope — inlined (frank-safe), same
          // idiom as the Suggestions button above.
          let scope = 'general'
          let n = el
          while (n) {
            if (n.chatScope) {
              scope = n.chatScope
              break
            }
            n = n.parent
          }
          const base =
            scope === 'addapp'
              ? '_ax_chatMessages'
              : scope === 'dashboard'
                ? '_db_chatMessages'
                : 'chatMessages'
          const list = (s.root && s.root[base]) || []
          const idx = list.findIndex((m) => m && m.id === s.id)
          let prompt = null
          for (let i = (idx < 0 ? list.length : idx) - 1; i >= 0; i--) {
            if (list[i] && list[i].role === 'user' && list[i].text) {
              prompt = list[i].text
              break
            }
          }
          if (!prompt) return
          try {
            el.call('sendPrompt', prompt, scope)
          } catch (_) {
            /* transport not ready — button is a no-op rather than a throw */
          }
        }
      },
      SwitchModelBtn: {
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        padding: 'Y Z1',
        minHeight: 'controlHCompact',
        round: 'radiusPill',
        theme: 'transparent',
        color: 'caption',
        fontSize: 'Z1',
        fontWeight: '500',
        cursor: 'pointer',
        border: 'none',
        transition: 'B defaultBezier',
        transitionProperty: 'background-color, color, transform',
        ':hover': { background: 'hover', color: 'title' },
        // "✦" matches AiModelSwitcherTrigger's own glyph (shared/components/
        // AiModelSwitcher.js) — no separate icon asset needed for this text
        // affordance.
        Label: { tag: 'span', text: '{{ ai.chat.switchModel | polyglot }}' },
        onClick: (e, el, s) => {
          e.preventDefault()
          if (s.root && s.root.update) s.root.update({ _modelMenuOpen: true })
        }
      }
    }
  }
}
